/* @flow */
// typecheck: ignore file
import BoxBody from 'components/BoxBody.jsx';
import CurrentUser from 'auth/CurrentUser';
import { debounce } from 'lodash';
import Dialog from 'components/Dialog.jsx';
import DialogButton from 'components/DialogButton.jsx';
import Divider from 'components/Divider.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatarSelector from './GroupAvatarSelector.jsx';
import GroupForm from './GroupForm.jsx';
import GroupOptionsForm from './GroupOptionsForm.jsx';
import GroupSchoolSelect from './GroupSchoolSelect.jsx';
import MediaObject from 'components/MediaObject.jsx';
import MessageHistoryDialog from 'user/components/MessageHistoryDialog.jsx';
import PromptActions from 'actions/PromptActions';
import PromptStore from 'stores/PromptStore';
import React, { Component } from 'react';
import SetUserEmailModal from 'settings/components/SetUserEmailModal.jsx';
import ToastActions from 'actions/ToastActions';
import { PromptNames } from 'constants/PromptConstants';
import { mountModal } from 'lib/modal';
import User from 'entities/User';
import Organization from 'entities/Organization';
import OrganizationStore from 'stores/OrganizationStore';
import { Container } from 'flux/utils';

import './styles/GroupCreateModal.scss';

type Props = {
  onCreate?: ?() => void,
  onRequestHide: () => void,
}

type State = {
  hasChildren: boolean,
  code: ?string,
  name: ?string,
  user: ?User,
  avatarHandpicked: boolean,
  groupCodeHandpicked: boolean,
  suggestedGroupCode: boolean,
  canSubmit: boolean,
  prominentOrg: boolean,
  initialOrganization: ?Organization,
}

const makeInitialState = function(): State {
  return {
    hasChildren: false,
    code: null,
    name: null,
    user: null,
    avatarHandpicked: false,
    groupCodeHandpicked: false,
    suggestedGroupCode: false,
    canSubmit: false,
    prominentOrg: CurrentUser.inExpVariant('PROMINENT_ORG', 'prominent_org'),
    initialOrganization: null,
  };
};

class GroupCreateModal extends Component {
  props: Props;
  state: State;

  static getStores() {
    return [CurrentUser, OrganizationStore];
  }

  static calculateState(prevState: ?State) {
    const user = CurrentUser.isSignedIn() ? CurrentUser.getUser() : null;
    const orgId = user ? user.getOrganizationId() : null;
    return {
      ...(prevState || makeInitialState()),
      user,
      initialOrganization: orgId ? OrganizationStore.getOrganization(orgId) : null,
    };
  }

  suggestGroupCode = debounce((name) => {
    Group
      .suggestCode(name)
      .then(codes => {
        if (!this.state.code && codes && codes[0]) {
          this.setState({ code: codes[0] });
          this.refs.groupForm.changeCodeTo(codes[0]);
        }
        this.setState({ suggestedGroupCode: true });
      })
      .catch(e => console.error('GroupCreateModal suggestCode failed', e));
  }, 300)

  suggestAvatarForName = debounce((name) => {
    if (!this || !this.refs || !this.refs.avatarSelector) {
      return;
    }
    this.refs.avatarSelector.searchAvatarForName(name);
  }, 300)

  onAvatarSelected = () => {
    this.setState({ avatarHandpicked: true });
  }

  onCodeChange = () => {
    if (!this.state.groupCodeHandpicked) {
      this.setState({ groupCodeHandpicked: true });
    }
  }

  onOptionsChange = (name, value) => {
    this.setState({ [name]: value });
  }

  onSubmit = () => {
    const orgData = {
      organization: this.state.prominentOrg ?
        this.refs.groupForm.getOrganizationForSubmit() :
        this.refs.groupSchoolMenu.getOrganizationForSubmit(),
    };

    const groupToCreate = new Group(
      Object.assign({
        name: this.state.code,
        class_name: this.state.name,
        group_avatar: {
          id: this.refs.avatarSelector.getSelectedAvatarId(),
        },
      }, this.refs.options.getValues(), orgData)
    );

    GroupActions
      .create(groupToCreate)
      .then(group => {
        this.props.onRequestHide();
        if (this.shouldShowMessageHistoryPrompt()) {
          // the setTimeout(..., 0) is necessary to avoid an infinite loop from the
          // callback to onRequestHide caused by the route change in this.props.onCreate
          mountModal(
            <MessageHistoryDialog
              onRequestHide={() => setTimeout(() => this.props.onCreate && this.props.onCreate(group), 0)}
              onConfirm={() => GroupActions.enableAllGroupMessageHistory()}
            />,
            this
          );
          PromptActions.recordDismissal(PromptNames.MESSAGE_HISTORY_MODAL);
        } else {
          if (this.props.onCreate) {
            this.props.onCreate(group);
          }
        }
      })
      .catch(ToastActions.showAPIError);
  }

  onFormInvalid = () => {
    // Prevent excessive re-renders
    if (this.state.canSubmit) {
      this.setState({ name: null, code: null, canSubmit: false });
    }
  }

  onFormValid = (name, code) => {
    this.setState({ name, code, canSubmit: true });
  }

  onValidNameChange = (name) => {
    if (!this.state.groupCodeHandpicked && !this.state.suggestedGroupCode) {
      this.suggestGroupCode(name);
    }

    if (!this.state.avatarHandpicked) {
      this.suggestAvatarForName(name);
    }
  }

  shouldShowMessageHistoryPrompt = () => {
    return CurrentUser.getUser().getPromptsInventory()
      .find(i => i.get('action') === PromptNames.MESSAGE_HISTORY_MODAL) &&
      !PromptStore.hasDismissed(PromptNames.MESSAGE_HISTORY_MODAL);
  }

  renderSchoolMenu() {
    return (
      <GroupSchoolSelect
        ref="groupSchoolMenu"
        hideUnaffiliated={this.state.hasChildren}
        initialOrganizationId={CurrentUser.getUser().getOrganizationId()}
        input="menu"
        prefix="At "
      />
    );
  }

  render() {
    const { user } = this.state;
    if (user && !user.hasEmail()) {
      return (
        <SetUserEmailModal
          {...this.props}
          closeOnSave={false}
          message={'An email address is required to create a group.'}
        />
      );
    }

    return (
      <Dialog
        eventScreenName="GroupCreateModal"
        {...this.props}
        className="GroupCreateModal"
        title="Create a class"
        subtext={!this.state.prominentOrg && this.renderSchoolMenu()}
        size="small">
        <BoxBody divided>
          <MediaObject className="GroupCreateModal-top">
            <div>
              <GroupAvatarSelector ref="avatarSelector" onSelect={this.onAvatarSelected} />
            </div>
            <div>
              <GroupForm
                ref="groupForm"
                onFormValid={this.onFormValid}
                onValidNameChange={this.onValidNameChange}
                onCodeChange={this.onCodeChange}
                onFormInvalid={this.onFormInvalid}
                hasChildren={this.state.hasChildren}
                showOrg={this.state.prominentOrg}
                currentOrganization={this.state.initialOrganization}
              />
            </div>
          </MediaObject>
          <Divider size="medium" />
          <GroupOptionsForm
            ref="options"
            subscriberInitiatedChats
            hasChildren={this.state.hasChildren}
            showSearchable={false}
            userIsTeacher={CurrentUser.getUser().isTeacher()}
            onChange={this.onOptionsChange}
          />
          <DialogButton
            className="GroupCreateModal-submitButton"
            disabled={!this.state.canSubmit}
            onClick={this.onSubmit}>
            Create
          </DialogButton>
        </BoxBody>
      </Dialog>
    );
  }
}

export default Container.create(GroupCreateModal, { withProps: true });



// WEBPACK FOOTER //
// ./src/groups/components/GroupCreateModal.jsx